// Portal de facturación de la suscripción (solo AR) — pantalla PROPIA full-screen,
// sin el shell/sidebar de la app, con el layout del customer portal de Stripe pero
// diseño Atlas (colores, bordes redondeados). Columna izquierda = branding + Volver;
// columna derecha = el contenido (suscripción, método, datos fiscales, historial).
// El botón "Volver" hace navigate(-1) → devuelve a donde estabas (ej. Configuración),
// con el guard de ADR-144 para no salirse de Atlas cuando no hay historial in-app.
// Ver ADR-2026-08-03-facturacion-portal-stripe-completo y ADR-2026-08-07-facturacion-volver-loop.

import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Receipt } from 'lucide-react';
import { supabase } from '../lib/supabase';
import {
  paisFlags,
  facturacionVisible,
  PARAM_HOME_POST_PAGO,
  resolverHomePostPago,
} from '../lib/checkoutPlans';
import { useUserGroup } from '../hooks/useUserGroup';
import { FacturacionSuscripcion } from './config_sections/FacturacionSuscripcion';
import { useCuentaAtlas } from '../hooks/useCuentaAtlas';
import SoloElDueno from '../components/SoloElDueno';

function FacturacionPortalDelDueno() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const demo = searchParams.get('demo') !== null;
  const [idCompany, setIdCompany] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const { isDevTester, grupo } = useUserGroup();
  // Último salto de la cadena post-pago: "Entrar a Atlas" tiene que entrar adonde dijo
  // quien cobró (el cuestionario, si vino del alta por link), no al estudio por grupo.
  // El param sobrevive el refresh del portal igual que `?postPago=1`, que está al lado.
  // Ver ADR-2026-09-22-el-destino-post-pago-sobrevive-la-cadena.
  const { homeDelLlamador, homePath } = resolverHomePostPago(
    searchParams.get(PARAM_HOME_POST_PAGO),
    isDevTester,
    grupo,
  );
  // Contexto post-pago: el usuario llegó recién de pagar (vía la oferta de facturación), no desde
  // Configuración. En ese caso mostramos "Entrar a Atlas" en vez de "Volver" (que lo devolvería al
  // checkout, dejándolo sin puerta de entrada). Ver ADR-2026-08-13-facturacion-pantalla-post-pago-ar.
  //
  // Llega por QUERY PARAM (?postPago=1) para SOBREVIVIR un refresh/reopen del portal: el location.state
  // se pierde al recargar y dejaba "Volver" (→ /configuracion) en vez de "Entrar a Atlas". Aceptamos
  // también el state por compatibilidad. Greptile PR#67.
  const postPago =
    searchParams.get('postPago') !== null ||
    (location.state as { postPago?: boolean } | null)?.postPago === true;

  useEffect(() => {
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (auth.user) {
        const { data: p } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', auth.user.id)
          .maybeSingle();
        const idc = (p as { id_company?: string | null } | null)?.id_company ?? null;
        setIdCompany(idc);
        // La facturación de la suscripción es SOLO Argentina. Si un usuario del exterior
        // llega a la ruta directo (el link del menú ya está limitado a AR), lo devolvemos
        // a la app en vez de mostrarle un portal que no le aplica. (El modo demo no gatea.)
        if (!demo && idc) {
          const { data: company } = await supabase
            .from('company_profiles')
            .select('pais, dev_tester')
            .eq('id_company', idc)
            .maybeSingle();
          const c = company as { pais?: string | null; dev_tester?: boolean | null } | null;
          const esAR = paisFlags(c?.pais).isArgentina;
          // Gate: solo AR, y solo si la facturación está habilitada (FACTURACION_ENABLED) o es
          // dev_tester. Oculta el portal a usuarios reales hasta que AFIP esté en prod (sigue en
          // homologación → facturas de prueba). Usamos el `dev_tester` AUTORITATIVO de la DB (no el de
          // useUserGroup, que en cache puede estar viejo/null y echaría a un dev tester válido antes de
          // resolver). Ver ADR-2026-08-13-facturacion-gate-dev-tester. Greptile PR#69.
          if (!facturacionVisible(esAR, c?.dev_tester === true)) {
            // `homeDelLlamador ?? '/'` y no `'/'` pelado: era la última fuga del destino
            // post-pago. Pasa cuando el país con el que se decidió cobrar por Rebill (la
            // URL + el país guardado) no coincide con `company_profiles.pais`, que el
            // backend deduce de la IP: VPN, geo caída, o el 'Otro' de respaldo. Sin
            // esto, el que pagó por el link termina en el estudio sin cuestionario —
            // justo el bug que este cambio cierra.
            // Va `homeDelLlamador` y NO `homePath`: el visitante normal (el que entra
            // desde Configuración) tiene que seguir yendo a '/', que resuelve el grupo
            // bien aunque la cache de `useUserGroup` esté vacía.
            // Ver ADR-2026-09-22-el-destino-post-pago-sobrevive-la-cadena.
            navigate(homeDelLlamador ?? '/', { replace: true });
            return;
          }
        }
      }
      setReady(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // POP (vuelve a donde estabas: Configuración, éxito de suscripción, lo que sea), pero nunca
  // a ciegas — guard de ADR-144. `key === 'default'` = deep-link/refresh directo en /facturacion:
  // no hay historial in-app y el back se iría fuera de Atlas. `state.sinHistorial` es el mismo
  // caso llegando desde el fallback del detalle (ahí el replace ya le dio una key propia).
  const volver = () => {
    if (location.key !== 'default' && !(location.state as { sinHistorial?: boolean } | null)?.sinHistorial) {
      navigate(-1);
      return;
    }
    navigate('/configuracion', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F4F6FB] md:flex">
      {/* Columna izquierda — branding + Volver (en desktop, columna fija; en mobile, barra superior) */}
      {/* `pt-[calc(env(safe-area-inset-top)…)]`: esta pantalla vive FUERA del shell
          (bloque "Protegidas SIN sidebar"), así que nadie le compensa la status bar
          translúcida del PWA standalone — sin esto, "Atlas" y "Volver" quedaban
          debajo de la hora (mismo arreglo que el header de /checkout). En desktop
          `md:py-10` pisa el calc; en browser mobile el inset es 0 y queda el py-4
          de siempre. */}
      <aside className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-black/[0.06] bg-white/90 px-5 pb-3 pt-[calc(env(safe-area-inset-top,0px)+0.75rem)] backdrop-blur md:h-screen md:w-[340px] md:shrink-0 md:flex-col md:items-start md:justify-start md:border-b-0 md:border-r md:bg-white md:px-10 md:py-10 md:backdrop-blur-none md:sticky md:top-0">
        <div className="flex items-center gap-2.5">
          <img src="/favicon.png" alt="Atlas" className="h-8 w-8 rounded-lg" />
          <span className="text-[15px] font-semibold text-neutral-900">Atlas</span>
        </div>

        <div className="mt-14 hidden md:block">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-[#2563F5] to-[#60E7F0] text-white shadow-[0_14px_28px_-14px_rgba(22,68,235,0.8)]">
            <Receipt className="h-6 w-6" />
          </span>
          <h1 className="mt-5 text-balance text-[28px] font-semibold leading-[1.12] tracking-[-0.01em] text-neutral-900">
            Facturación
            <br />
            de tu plan
          </h1>
          <p className="mt-3 max-w-[260px] text-[14px] leading-relaxed text-neutral-600">
            Gestioná tu suscripción y descargá las facturas AFIP de Atlas.
          </p>
        </div>

        {postPago ? (
          <button
            onClick={() => navigate(homePath, { replace: true })}
            className="inline-flex h-9 items-center gap-1.5 self-center rounded-xl bg-brand-primary px-3.5 text-[14px] font-semibold text-white shadow-[0_10px_20px_-12px_rgba(22,68,235,0.8)] transition-colors hover:bg-brand-primary-hover md:mt-8 md:h-10 md:self-start md:px-4"
          >
            Entrar a Atlas <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button
            onClick={volver}
            className="inline-flex h-9 items-center gap-1.5 self-center rounded-xl px-3 text-[14px] font-medium text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-neutral-900 md:-ml-3 md:mt-8 md:self-start"
          >
            <ArrowLeft className="h-4 w-4" /> Volver
          </button>
        )}
      </aside>

      {/* Columna derecha — el contenido del portal */}
      <main className="min-h-screen min-w-0 flex-1">
        <div className="mx-auto max-w-2xl px-4 py-6 md:px-10 md:py-12">
          {/* En mobile el título va arriba del contenido (en desktop está en la columna izquierda) */}
          <h1 className="mb-1 text-[24px] font-semibold tracking-[-0.01em] text-neutral-900 md:hidden">Facturación de tu plan</h1>
          <p className="mb-5 text-[13.5px] text-neutral-500 md:hidden">Tu suscripción y las facturas AFIP de tu plan.</p>

          {ready || demo ? (
            <FacturacionSuscripcion profile={{ id_company: idCompany }} demo={demo} />
          ) : (
            <div className="h-40" />
          )}
        </div>
      </main>
    </div>
  );
}

/** Facturación es plata: sólo el dueño (ADR-2026-10-19-permisos-por-miembro-del-equipo). Mismo
 *  guard que `Paywall` y `Checkout`; antes un miembro entraba por URL y cambiaba el CUIT o la tarjeta. */
export default function FacturacionPortal() {
  const cuenta = useCuentaAtlas();
  if (cuenta.cargando) return null;
  if (!cuenta.esDueno) return <SoloElDueno />;
  return <FacturacionPortalDelDueno />;
}
